import type { FormAnswer, FormField, FormFieldType } from '../fixtures/domain.js';
import { answerValues } from '../data/forms.js';
import { Icon, type IconName } from '../components/Icon.js';
import styles from './FormPreview.module.css';

type OptionType = Exclude<FormFieldType, 'text'>;

const MARK: Record<OptionType, Record<'off' | 'on', IconName>> = {
  choice: { off: 'radio', on: 'radioOn' },
  multiChoice: { off: 'tickbox', on: 'tickboxOn' },
};

/**
 * A form as the person answering it will see it, and inert. Shared by the
 * library pane, the send dialog, and the thread card. With `answers`, the picks
 * and written words are drawn in; with `pickedOnly`, only what was answered.
 */
export function FormPreview({
  fields,
  answers,
  pickedOnly = false,
}: {
  fields: FormField[];
  answers?: FormAnswer[];
  pickedOnly?: boolean;
}) {
  const picked = (fieldId: string, optionId: string) =>
    answers?.some((answer) => answer.fieldId === fieldId && answer.optionIds.includes(optionId)) ??
    false;

  return (
    <ol className={styles.questions}>
      {fields.map((field) => (
        <li key={field.id} className={styles.question}>
          <p className={styles.label}>
            <span>{field.label}</span>
            {/* Said only where several answers is not the default assumption. */}
            {!pickedOnly && field.type === 'multiChoice' && (
              <span className={styles.hint}>Choose any that apply</span>
            )}
          </p>
          {!pickedOnly && field.description && (
            <p className={styles.description}>{field.description}</p>
          )}
          {field.type === 'text' ? (
            <TextAnswer text={answers ? (answerValues(field, answers)[0] ?? null) : null} />
          ) : (
            <ul className={styles.options}>
              {(pickedOnly
                ? field.options.filter((option) => picked(field.id, option.id))
                : field.options
              ).map((option) => {
                const on = picked(field.id, option.id);
                const marks = MARK[field.type as OptionType];
                return (
                  <li key={option.id} className={on ? styles.optionPicked : styles.option}>
                    <span className={on ? styles.markPicked : styles.mark}>
                      <Icon name={marks[on ? 'on' : 'off']} size={13} />
                    </span>
                    {option.label}
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}

/** A written answer is plain words; unanswered, an option row's shape with the pen. */
function TextAnswer({ text }: { text: string | null }) {
  return text ? (
    <p className={styles.textAnswer}>{text}</p>
  ) : (
    <p className={styles.textBlank}>
      <span className={styles.mark}>
        <Icon name="penLine" size={13} />
      </span>
      Text answer
    </p>
  );
}
